import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from "react";
import { useTranslation } from "react-i18next";
import { InstallationPickerSheet } from "../installation/InstallationPickerSheet";
import {
  buildDecimalCentOptions,
  buildIntegerTonnesOptions,
  formatDecimalCentLabel,
  formatWeightFromWheelParts,
  parseWeightToWheelParts,
} from "./photoCaptureWeightUtils";

const WHEEL_ITEM_HEIGHT = 44;

function getIndexFromScrollTop(scrollTop) {
  return Math.round(scrollTop / WHEEL_ITEM_HEIGHT);
}

function getScrollTopForIndex(index) {
  return index * WHEEL_ITEM_HEIGHT;
}

function PhotoCaptureWeightWheelColumn({
  options,
  selectedValue,
  onSelect,
  label,
  formatLabel = (option) => String(option),
  active = true,
}) {
  const scrollRef = useRef(null);
  const scrollEndTimerRef = useRef(null);
  const isProgrammaticScrollRef = useRef(false);

  const syncScrollToValue = useCallback(
    (value, behavior = "auto") => {
      const scrollElement = scrollRef.current;
      if (!scrollElement) {
        return;
      }

      const selectedIndex = options.indexOf(value);
      if (selectedIndex < 0) {
        return;
      }

      isProgrammaticScrollRef.current = true;
      scrollElement.scrollTo({
        top: getScrollTopForIndex(selectedIndex),
        behavior,
      });
      window.requestAnimationFrame(() => {
        isProgrammaticScrollRef.current = false;
      });
    },
    [options],
  );

  useLayoutEffect(() => {
    if (!active) {
      return;
    }
    syncScrollToValue(selectedValue);
  }, [active, selectedValue, syncScrollToValue]);

  const commitScrollPosition = useCallback(() => {
    const scrollElement = scrollRef.current;
    if (!scrollElement || isProgrammaticScrollRef.current) {
      return;
    }

    const selectedIndex = getIndexFromScrollTop(scrollElement.scrollTop);
    const snappedScrollTop = getScrollTopForIndex(selectedIndex);
    if (Math.abs(scrollElement.scrollTop - snappedScrollTop) > 1) {
      isProgrammaticScrollRef.current = true;
      scrollElement.scrollTop = snappedScrollTop;
      window.requestAnimationFrame(() => {
        isProgrammaticScrollRef.current = false;
      });
    }

    const nextValue = options[selectedIndex];
    if (nextValue !== undefined && nextValue !== selectedValue) {
      onSelect(nextValue);
    }
  }, [onSelect, options, selectedValue]);

  const handleScroll = () => {
    if (isProgrammaticScrollRef.current) {
      return;
    }

    window.clearTimeout(scrollEndTimerRef.current);
    scrollEndTimerRef.current = window.setTimeout(commitScrollPosition, 80);
  };

  useEffect(
    () => () => {
      window.clearTimeout(scrollEndTimerRef.current);
    },
    [],
  );

  return (
    <div className="photo-capture-weight-wheel-column">
      <span className="photo-capture-weight-wheel-label">{label}</span>
      <div className="photo-capture-weight-wheel-viewport">
        <div
          ref={scrollRef}
          className="photo-capture-weight-wheel-scroll"
          onScroll={handleScroll}
          onTouchEnd={commitScrollPosition}
          onMouseUp={commitScrollPosition}
        >
          {options.map((option) => (
            <div
              key={`${label}-${option}`}
              className="photo-capture-weight-wheel-item"
            >
              {formatLabel(option)}
            </div>
          ))}
        </div>
        <div className="photo-capture-weight-wheel-highlight" aria-hidden />
      </div>
    </div>
  );
}

const DEFAULT_WEIGHT_PICKER_DRAFT = "2.5";

function getWeightPickerDraft(savedValue) {
  const trimmed = String(savedValue ?? "").trim();
  return trimmed || DEFAULT_WEIGHT_PICKER_DRAFT;
}

const PhotoCaptureWeightWheelPicker = ({
  open,
  value,
  onChange,
  onDismiss,
}) => {
  const { t } = useTranslation();
  const [draftWeight, setDraftWeight] = useState(value);
  const integerOptions = buildIntegerTonnesOptions();
  const decimalOptions = buildDecimalCentOptions();
  const { integerPart, decimalPart } = parseWeightToWheelParts(draftWeight);

  useEffect(() => {
    if (open) {
      setDraftWeight(getWeightPickerDraft(value));
    }
  }, [open, value]);

  const handleIntegerChange = (nextInteger) => {
    setDraftWeight(formatWeightFromWheelParts(nextInteger, decimalPart));
  };

  const handleDecimalChange = (nextDecimal) => {
    setDraftWeight(formatWeightFromWheelParts(integerPart, nextDecimal));
  };

  const handleCancel = () => {
    onDismiss();
  };

  const handleValidate = () => {
    onChange(draftWeight);
    onDismiss();
  };

  return (
    <InstallationPickerSheet
      open={open}
      title={t("Weight")}
      onSheetCancel={handleCancel}
      onSheetValidate={handleValidate}
    >
      <div className="photo-capture-weight-wheel-picker">
        <div className="photo-capture-weight-wheel-row">
          <PhotoCaptureWeightWheelColumn
            active={open}
            label={t("mobile_photo_weight_wheel_integer")}
            options={integerOptions}
            selectedValue={integerPart}
            onSelect={handleIntegerChange}
          />
          <span className="photo-capture-weight-wheel-separator">,</span>
          <PhotoCaptureWeightWheelColumn
            active={open}
            label={t("mobile_photo_weight_wheel_decimal")}
            options={decimalOptions}
            selectedValue={decimalPart}
            onSelect={handleDecimalChange}
            formatLabel={formatDecimalCentLabel}
          />
          <span className="photo-capture-weight-wheel-unit">{t("tons")}</span>
        </div>
      </div>
    </InstallationPickerSheet>
  );
};

export default PhotoCaptureWeightWheelPicker;
